import { Button, TextInput, Textarea } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import Page from "@/components/Page.tsx";

import useAuth from "@/hooks/useAuth.ts";

import { Email } from "@/models/base/email.ts";
import { Status } from "@/models/base/general.ts";
import { EmailDto } from "@/models/internal/email-dto.ts";

import { copyIdToken } from "@/services/authService.ts";
import { addEmail } from "@/services/emailService.ts";

import { dayjs } from "@/utils/dayjsSetup.ts";

interface FormValues {
    email: string;
    date: Date;
    title: string;
    content: string;
}

const HomePage: FC = () => {
    const { isAdmin } = useAuth();
    const { t, i18n } = useTranslation();
    const form = useForm<FormValues>({
        initialValues: {
            email: "",
            date: dayjs().add(1, "year").toDate(),
            title: "",
            content: "",
        },

        validateInputOnBlur: true,
        validate: {
            email: (value) => (/^\S+@\S+$/.test(value) ? undefined : t("validation.invalidEmail")),
            date: (value) => (dayjs(value).isValid() ? undefined : t("validation.invalidDate")),
            title: (value) => (value.length > 0 ? undefined : t("validation.required")),
            content: (value) => (value.length > 0 ? undefined : t("validation.required")),
        },
    });

    const onSubmit = async (values: FormValues): Promise<void> => {
        const emailDto: EmailDto = {
            targetEmail: values.email,
            title: values.title,
            content: values.content,
            day: dayjs(values.date).format("YYYY-MM-DD"),
            status: Status.ACTIVE,
        };

        const email: Email | undefined = await addEmail(emailDto);

        if (!email) {
            console.warn("Failed to create email");
        }

        form.reset();
        console.debug(email);
    };

    return (
        <Page fullScreen>
            <form onSubmit={form.onSubmit(onSubmit)}>
                <TextInput type="email" label="To" placeholder="example@example.com" {...form.getInputProps("email")} />
                <DateInput
                    valueFormat="DD.MM.YYYY"
                    locale={i18n.language}
                    minDate={new Date()}
                    label="Email Send Date"
                    {...form.getInputProps("date")}
                />
                <TextInput label="Title" placeholder="My Title" {...form.getInputProps("title")} />
                <Textarea label="Content" placeholder="My Content" {...form.getInputProps("content")} />
                <Button type="submit">{t("email.create")}</Button>
            </form>
            {isAdmin && <Button onClick={copyIdToken}>{t("admin.copyToken")}</Button>}
        </Page>
    );
};
export default HomePage;
